// A barra de baixo do telemóvel — um carrossel infinito de destinos.
//
// Ideia: no telemóvel a app deixa de se navegar por um menu que abre e fecha e
// passa a navegar-se com o polegar, na zona onde o polegar chega. Arrasta-se de
// lado para ver todos os destinos e toca-se num para o abrir.
//
// A regra que segura tudo: o ícone aceso a dourado é o da PÁGINA ABERTA, e não
// o que calha estar ao meio. Deslizar não acende nada e não carrega nada — só
// mostra. Quem desliza está a procurar, não a escolher, e a barra não finge o
// contrário acendendo tudo por onde o polegar passa.
//
// Como é que roda sem fim:
//
// A lista tem N destinos e são desenhadas COPIAS cópias dela em fila. O
// utilizador vive na cópia do meio, com quatro cópias de folga de cada lado —
// muito mais do que um polegar percorre de uma vez. Quando o scroll assenta,
// saltamos de volta para a cópia do meio: o conteúdo é idêntico ao pixel, por
// isso o salto não se vê. Fazê-lo só depois de assentar é o ponto todo — mexer
// no scrollLeft a meio da inércia mata a inércia no iOS.
//
// O scroll é o do browser (snap + inércia nativa), não um arrastar contado à
// mão. Num telemóvel, a diferença entre os dois sente-se no primeiro gesto.

import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { animate, motion, useMotionValue, useReducedMotion } from "framer-motion";
import { LINKS, indiceAtivo } from "@/components/ui/ligacoes";
import { useUnreadCount } from "@/api/queries";

const N = LINKS.length;
const COPIAS = 9;
/** Primeiro lugar da cópia do meio — para onde voltamos quando o scroll para. */
const MEIO = Math.floor(COPIAS / 2) * N;
/** Largura de cada lugar, em px. Num telemóvel cabem cinco: o do meio e dois de cada lado. */
const LARGURA = 76;
/** Lado do ícone — e, por isso, da gota dourada que se lhe encosta por baixo. */
const PILULA = 44;
/** Folga por baixo do ícone, para a barra não o encostar ao fundo do ecrã. */
const FUNDO = 6;
/** Quanto cresce o que está aceso. A gota e o ícone crescem o mesmo. */
const REALCE = 1.16;
/** Largura de uma cópia inteira da lista — o passo com que tudo se repete. */
const LAPSO = N * LARGURA;
/** Sem eventos de scroll durante este tempo, damos o gesto por terminado. */
const ASSENTAR = 180;
/** Até este ponto ainda se está "no topo" da página — e as etiquetas ficam. */
const LIMIAR_TOPO = 12;

const LUGARES = Array.from({ length: COPIAS * N }, (_, i) => i);

/** O destino a que um lugar corresponde (os lugares repetem-se de N em N). */
function destino(lugar: number): number {
  return ((lugar % N) + N) % N;
}

/** Onde a gota assenta, em coordenadas da fila, para ficar debaixo do ícone. */
function pousoDaGota(lugar: number): number {
  return lugar * LARGURA + (LARGURA - PILULA) / 2;
}

/**
 * Um lugar da fila.
 *
 * Memoizado, e as duas coisas que mudam sozinhas ficam de fora: `ativo` só
 * muda quando se muda de página, e as etiquetas aparecem e desaparecem por
 * `data-etiquetas` no elemento de cima. O resultado é que deslizar a barra —
 * o gesto mais frequente que ela tem — não renderiza rigorosamente nada.
 */
const Lugar = memo(function Lugar({
  lugar,
  ativo,
  porLer,
  aoTocar,
}: {
  lugar: number;
  ativo: boolean;
  /** Avisos por ler — só o lugar dos Avisos lhe liga (IDEA-044). */
  porLer: number;
  aoTocar: (lugar: number) => void;
}) {
  const item = LINKS[destino(lugar)];
  const Icone = item.icone;
  // O ponto vive no lugar dos Avisos e em mais nenhum. Passa por todos porque
  // todos são o mesmo componente; só um o desenha.
  const marca = item.href === "/notificacoes" && porLer > 0;
  // Só a cópia do meio conta para o teclado e para o leitor de ecrã — caso
  // contrário seriam COPIAS × N botões para N destinos.
  const canonico = lugar >= MEIO && lugar < MEIO + N;

  return (
    <button
      type="button"
      onClick={() => aoTocar(lugar)}
      aria-label={marca ? `${item.etiqueta}, ${porLer} por ler` : item.etiqueta}
      aria-current={ativo && canonico ? "page" : undefined}
      aria-hidden={canonico ? undefined : true}
      tabIndex={canonico ? 0 : -1}
      style={{ width: LARGURA, paddingBottom: FUNDO }}
      // O ícone encosta-se a baixo em vez de ficar ao meio do lugar: a etiqueta
      // vive por cima dele, e mesmo o ícone do meio — que cresce — não lhe toca.
      //
      // Sem etiqueta, os 16px que ela ocupava não têm de continuar ali: o lugar
      // encolhe e a placa encolhe com ele. Como a placa está agarrada ao fundo
      // do ecrã, quem desce é a aresta de cima — os ícones não se mexem, a
      // página é que ganha o espaço. O `delay` deixa a etiqueta apagar-se
      // primeiro e só depois encolhe; ao contrário, cresce primeiro e a
      // etiqueta acende-se depois de haver sítio para ela.
      className="relative flex h-[72px] shrink-0 snap-center flex-col items-center justify-end outline-none transition-[height] duration-300 ease-out group-data-[etiquetas=nao]:h-[56px] group-data-[etiquetas=nao]:delay-150"
    >
      {/* O dourado já não está aqui: é uma gota só, que viaja por baixo dos
          ícones (ver a barra). Aqui fica o que a gota faz ao ícone quando lhe
          chega — cresce e passa a creme.

          O `delay` é o que impede o creme sobre creme: quando um ícone se
          acende espera que a gota chegue, e quando se apaga não espera nada,
          porque nessa altura a gota já lhe saiu de baixo. */}
      <span
        style={{ width: PILULA, height: PILULA }}
        className={`flex items-center justify-center transition-all duration-200 ease-out ${
          ativo ? "scale-[1.16] text-creme-50 delay-200" : "scale-90 text-tinta-500 opacity-70"
        }`}
      >
        <Icone className="h-6 w-6" />
      </span>
      {/* O ponto dos avisos por ler. Encostado ao canto do ícone e sem número:
          num lugar de 44px um "12" não se lê, e o que aqui interessa é que há
          alguma coisa nova — quantas, diz a página. O anel creme separa-o do
          ícone quando a gota dourada está por baixo dos dois. */}
      {marca && (
        <span
          aria-hidden
          className={`pointer-events-none absolute right-[18px] top-[26px] h-2.5 w-2.5 rounded-full border-2 border-creme-100 transition-colors group-data-[etiquetas=nao]:top-[10px] ${
            ativo ? "bg-creme-50" : "bg-ouro"
          }`}
        />
      )}
      {/* A etiqueta está lá por omissão — o ícone sozinho não chega para se
          saber o que é. Sai da frente quando se começa a ler a página e volta
          ao chegar-se outra vez ao topo. Por cima do ícone, onde o polegar que
          arrasta a barra não a tapa. */}
      <span
        className={`pointer-events-none absolute inset-x-0 top-1 truncate px-1 text-[10px] font-semibold leading-none transition-opacity delay-200 duration-200 group-data-[etiquetas=nao]:opacity-0 group-data-[etiquetas=nao]:delay-0 ${
          ativo ? "text-ouro-600" : "text-tinta-500"
        }`}
      >
        {item.etiqueta}
      </span>
    </button>
  );
});

export function BarraInferior() {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const reduzir = useReducedMotion();
  const porLer = useUnreadCount().data?.naoLidas ?? 0;

  const pista = useRef<HTMLDivElement>(null);
  const [noTopo, setNoTopo] = useState(true);

  // Que lugar está ao meio é escrituração, não estado: nada no ecrã depende
  // disso. Serve para saber onde estamos na fila (normalizar a cópia, medir o
  // caminho mais curto até um destino, ver se a barra já parou) — e por ser um
  // ref, deslizar a barra não faz um único render.
  const centroRef = useRef(MEIO + indiceAtivo(pathname));
  // Um salto instantâneo também dispara `scroll`; este sinal impede que o
  // salto que normaliza a fila se volte a agendar a si próprio.
  const aSaltar = useRef(false);
  const relogio = useRef<number | undefined>(undefined);

  // A gota dourada. Vive em coordenadas da FILA, não do ecrã: é filha da pista,
  // por isso desliza com ela de graça e a viagem dela não tem de saber nada
  // sobre o scroll que acontece ao mesmo tempo.
  //
  // Gota no singular é como se vê; no DOM são COPIAS, uma por cópia da lista,
  // todas presas ao mesmo `x` e afastadas de um LAPSO exacto. É isto que faz o
  // dourado sobreviver a uma volta completa ao carrossel: a página aberta tem
  // uma gota em TODAS as suas cópias, e não só naquela em que calhou ficar.
  // Uma gota só ficava para trás — dar a volta trazia outra cópia do mesmo
  // ícone, aceso mas sem dourado por baixo.
  //
  // `gotaLugar` é o lugar dela dentro da cópia base, não na fila toda.
  const gotaLugar = useRef(indiceAtivo(pathname));
  const gotaX = useMotionValue(pousoDaGota(gotaLugar.current));
  const gotaEscala = useMotionValue(REALCE);
  const gotaViagem = useRef<{ stop: () => void } | null>(null);

  const moverGota = useCallback(
    (alvo: number, viajar: boolean) => {
      gotaViagem.current?.stop();

      // Recolher a gota à cópia base antes de a atirar. Voltas seguidas ao
      // mesmo lado empurram-na uma cópia de cada vez e, ao fim de umas
      // quantas, sairia das cópias desenhadas. Recolhê-la é deslocá-la um
      // número inteiro de LAPSOS — e como as gotas se repetem exactamente de
      // LAPSO em LAPSO, no ecrã não se mexe nada.
      const partida = ((gotaLugar.current % N) + N) % N;
      if (partida !== gotaLugar.current) {
        gotaX.jump(gotaX.get() + (partida - gotaLugar.current) * LARGURA);
        gotaLugar.current = partida;
      }

      // Pelo caminho curto. Qualquer múltiplo de N daria no mesmo sítio — os
      // destinos repetem-se —, mas só o mais curto é uma gota a saltar de um
      // ícone para o vizinho em vez de atravessar a barra de ponta a ponta.
      const meia = Math.floor(N / 2);
      const passo = ((alvo - partida + N + meia) % N) - meia;
      gotaLugar.current = partida + passo;
      const pouso = pousoDaGota(gotaLugar.current);

      if (!viajar || reduzir || passo === 0) {
        gotaX.jump(pouso);
        return;
      }
      // Mola com um resto de balanço à chegada, e um encolher a meio caminho:
      // é o que faz a coisa parecer uma gota a ser atirada de um ícone para o
      // outro, e não um rectângulo a mudar de sítio.
      gotaViagem.current = animate(gotaX, pouso, {
        type: "spring",
        stiffness: 380,
        damping: 30,
        mass: 0.9,
      });
      animate(gotaEscala, [REALCE, REALCE * 0.88, REALCE], {
        duration: 0.42,
        times: [0, 0.45, 1],
        ease: "easeInOut",
      });
    },
    [gotaX, gotaEscala, reduzir],
  );

  const centrar = useCallback((lugar: number, suave: boolean) => {
    const el = pista.current;
    if (!el) return;
    const alvo = lugar * LARGURA + LARGURA / 2 - el.clientWidth / 2;
    centroRef.current = lugar;
    if (!suave) {
      aSaltar.current = true;
      window.setTimeout(() => {
        aSaltar.current = false;
      }, 120);
    }
    el.scrollTo({ left: alvo, behavior: suave ? "smooth" : "auto" });
  }, []);

  // Posição inicial: o destino da página aberta, na cópia do meio.
  useLayoutEffect(() => {
    centrar(MEIO + indiceAtivo(pathname), false);
    // Só à entrada — daí em diante é o efeito da rota que manda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quem está ao meio sai do scrollLeft, e não de um arrastar contado à mão.
  // Repare-se que não há `setState` nenhum aqui dentro: deslizar a barra não
  // muda nada no ecrã, só a conta de onde estamos.
  useEffect(() => {
    const el = pista.current;
    if (!el) return;
    let pedido = 0;

    function assentou() {
      // De volta à cópia do meio, para nunca se chegar à ponta da fila.
      const lugar = centroRef.current;
      const normal = MEIO + destino(lugar);
      if (normal === lugar) return;
      // As gotas não precisam de saber disto. O salto é de um número inteiro de
      // cópias e elas repetem-se com esse mesmo passo, por isso a fila desloca-
      // -se por baixo delas e o desenho fica igual ao que estava.
      centrar(normal, false);
    }

    function aoScroll() {
      if (pedido) return;
      pedido = requestAnimationFrame(() => {
        pedido = 0;
        const atual = pista.current;
        if (!atual) return;
        const lugar = Math.round(
          (atual.scrollLeft + atual.clientWidth / 2 - LARGURA / 2) / LARGURA,
        );
        centroRef.current = lugar;
        if (aSaltar.current) return;
        window.clearTimeout(relogio.current);
        relogio.current = window.setTimeout(assentou, ASSENTAR);
      });
    }

    el.addEventListener("scroll", aoScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", aoScroll);
      if (pedido) cancelAnimationFrame(pedido);
      window.clearTimeout(relogio.current);
    };
  }, [centrar]);

  // As etiquetas seguem o scroll da PÁGINA, não o da barra: estão lá enquanto
  // se está no topo e saem da frente assim que se começa a ler. Quem está a ler
  // não está a navegar, e a barra encolhe-se para o que é — uma fila de
  // ícones. Ao
  // voltar ao topo, voltam elas.
  useEffect(() => {
    let pedido = 0;
    function avaliar() {
      setNoTopo(window.scrollY <= LIMIAR_TOPO);
    }
    function aoScrollPagina() {
      if (pedido) return;
      pedido = requestAnimationFrame(() => {
        pedido = 0;
        avaliar();
      });
    }
    avaliar();
    window.addEventListener("scroll", aoScrollPagina, { passive: true });
    return () => {
      window.removeEventListener("scroll", aoScrollPagina);
      if (pedido) cancelAnimationFrame(pedido);
    };
  }, []);

  // Rodar o telemóvel muda a largura da pista — e com ela a conta do meio.
  useEffect(() => {
    function aoRedimensionar() {
      centrar(centroRef.current, false);
    }
    window.addEventListener("resize", aoRedimensionar);
    return () => window.removeEventListener("resize", aoRedimensionar);
  }, [centrar]);

  // Mudar de página — por um toque na barra ou por um link de dentro da página
  // (um widget do Início, por exemplo) — atira a gota para o ícone novo e traz
  // esse ícone ao meio. Vai-se pelo caminho mais curto: a cópia mais próxima,
  // não a do meio, senão a barra dava a volta ao mundo por nada.
  //
  // Isto serve os dois casos de uma vez. Quando o toque foi na barra, a conta
  // do caminho mais curto dá exactamente o lugar tocado — só se tocam ícones à
  // vista, e esses estão a dois lugares do meio, quando muito.
  useEffect(() => {
    const alvo = indiceAtivo(pathname);
    const atual = centroRef.current;
    const meia = Math.floor(N / 2);
    const passo = ((alvo - destino(atual) + N + meia) % N) - meia;
    const lugarAlvo = atual + passo;
    moverGota(alvo, true);
    if (lugarAlvo !== atual) centrar(lugarAlvo, !reduzir);
  }, [pathname, centrar, moverGota, reduzir]);

  const aoTocar = useCallback(
    (lugar: number) => {
      // Deslizar mostra; tocar escolhe. Qualquer ícone que esteja à vista abre
      // ao primeiro toque — não há um lugar do meio com poderes especiais,
      // porque já não há nada que o distinga a olho: o dourado está na página
      // aberta, e não em quem passa por ali.
      //
      // A excepção é o toque dado para travar a inércia. Esse cai em cima de um
      // ícone qualquer e não pode abrir uma página que ninguém pediu — por isso
      // só se abre com a barra encostada a um lugar, parada.
      const el = pista.current;
      if (!el) return;
      const exato = centroRef.current * LARGURA + LARGURA / 2 - el.clientWidth / 2;
      if (Math.abs(el.scrollLeft - exato) >= 6) return;

      // A página aberta traz-se de volta ao meio; qualquer outra abre-se (e o
      // efeito da rota trata de a trazer ao meio a seguir).
      if (destino(lugar) === indiceAtivo(pathname)) {
        centrar(lugar, !reduzir);
        return;
      }
      navigate(LINKS[destino(lugar)].href);
    },
    [centrar, navigate, pathname, reduzir],
  );

  // O dourado é da página aberta, e de mais ninguém. Passar por cima de um
  // ícone não o acende — só abri-lo o acende. Todas as cópias do mesmo destino
  // ficam acesas, mas nunca se vêem duas ao mesmo tempo: há mais destinos do
  // que os cinco que cabem no ecrã.
  const aceso = indiceAtivo(pathname);

  return (
    <>
      {/* O véu.
          A placa é opaca, mas o problema não era o que passava por baixo dela —
          era o que passava à volta: uma linha de texto legível na frincha entre
          a placa e o fundo do ecrã, e outra a espreitar por cima. Uma barra a
          flutuar precisa de flutuar sobre alguma coisa.

          Daí uma faixa desfocada no fundo do ecrã, a esbater-se para cima, que
          é o que separa o que se lê do que se toca. Não recebe toques: sem
          isto, os últimos centímetros de cada página deixavam de responder.

          A faixa acaba exactamente onde a placa acaba — a folga de baixo mais a
          altura dela, e por isso encolhe quando ela encolhe. Subir mais alto
          via-se: o desfocado assomava por cima da barra, sem nada que o
          explicasse. O esbatimento cai todo dentro da altura da placa, que é
          opaca; quem o vê são as margens dos lados, e é lá que ele faz falta. */}
      <div
        aria-hidden
        className={`pointer-events-none fixed inset-x-0 bottom-0 z-30 bg-gradient-to-t from-creme via-creme/80 to-transparent backdrop-blur-[6px] transition-[height] duration-300 ease-out [mask-image:linear-gradient(to_top,black_45%,transparent)] sm:hidden ${
          noTopo
            ? "h-[calc(6rem+env(safe-area-inset-bottom))]"
            : "h-[calc(5rem+env(safe-area-inset-bottom))] delay-150"
        }`}
      />
      <nav
        aria-label="Navegação"
        // Uma placa a flutuar, e não uma faixa colada ao fundo do ecrã: margem
        // dos dois lados e por baixo, e cantos redondos. O `overflow-hidden` é o
        // que obriga a fila a respeitar os cantos — sem ele os ícones saíam por
        // eles fora.
        //
        // Opaca, e não fosca. Chegou a ser vidro desfocado, mas uma fotografia a
        // passar por trás continuava a ver-se, e uma barra de navegação não é
        // sítio para haver duas coisas ao mesmo tempo. Um creme um tom acima do
        // da página, com a linha e a sombra, chega para se perceber que está por
        // cima. (Sendo opaca, o `backdrop-blur` já não pintava nada — saiu.)
        className="fixed bottom-[calc(0.75rem+env(safe-area-inset-bottom))] left-3 right-3 z-40 overflow-hidden rounded-3xl border border-ouro/25 bg-creme-100 py-1.5 shadow-[0_16px_36px_-16px_rgba(59,47,42,0.5)] sm:hidden"
      >
        <div
          ref={pista}
          data-etiquetas={noTopo ? "sim" : "nao"}
          // A máscara desvanece os ícones nas pontas: a fila continua para lá do
          // ecrã, e vê-se que continua.
          className="scroll-x-suave group relative flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain [mask-image:linear-gradient(to_right,transparent,black_16%,black_84%,transparent)]"
        >
          {/* As gotas. Primeiras na ordem do DOM de propósito: os botões vêm a
            seguir e pintam-se por cima delas, que é o que as põe por baixo dos
            ícones sem precisar de z-index.

            Uma por cópia da lista, mas um só `x`: quem viaja é este invólucro,
            e elas vão à boleia, sempre a um LAPSO certo umas das outras. Assim
            a página aberta está dourada em qualquer cópia onde se pare.

            Presas a baixo, e não a cima: assim a altura da barra pode encolher
            e crescer à vontade que ficam debaixo dos ícones, sem ninguém ter de
            lhes recalcular nada. */}
          <motion.div
            aria-hidden
            style={{ x: gotaX }}
            className="pointer-events-none absolute inset-y-0 left-0 w-0"
          >
            {Array.from({ length: COPIAS }, (_, copia) => (
              <motion.div
                key={copia}
                style={{
                  left: copia * LAPSO,
                  scale: gotaEscala,
                  bottom: FUNDO,
                  width: PILULA,
                  height: PILULA,
                }}
                className="absolute rounded-2xl bg-ouro shadow-[0_10px_20px_-8px_rgba(201,162,39,0.85)]"
              />
            ))}
          </motion.div>
          {LUGARES.map((lugar) => (
            <Lugar
              key={lugar}
              lugar={lugar}
              ativo={destino(lugar) === aceso}
              porLer={porLer}
              aoTocar={aoTocar}
            />
          ))}
        </div>
      </nav>
    </>
  );
}
